import { Link } from "@tanstack/react-router";
import { format } from "date-fns";

import {
  useDecisionDetail,
  useUpdateDecision,
} from "@/application/use-cases/decisions/use-decisions.queries";
import { useMeetingsForEntity } from "@/application/use-cases/meetings/use-meetings.queries";
import { StatusDot, STATUS_LABEL } from "@/presentation/components/status/status-dot";
import { Input } from "@monorepo-apps/ui/src/components/input";
import { Textarea } from "@monorepo-apps/ui/src/components/textarea";
import { Label } from "@monorepo-apps/ui/src/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@monorepo-apps/ui/src/components/select";
import type { Status } from "@/infrastructure/supabase/database.types";

export function DecisionDrawerContent({ id }: { id: string }) {
  const detailQ = useDecisionDetail(id);
  const meetingsQ = useMeetingsForEntity("decision", id);
  const update = useUpdateDecision();

  if (!detailQ.data) {
    return <p className="text-muted-foreground px-6 py-8 text-xs italic">Loading…</p>;
  }
  const d = detailQ.data;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <header className="border-border/60 flex flex-col gap-1.5 border-b px-6 py-5">
        <span className="eyebrow inline-flex items-center gap-2">
          <StatusDot tone={d.status} /> {STATUS_LABEL[d.status]}
          {d.gate ? ` · ${d.gate}` : null}
        </span>
        <h2 className="text-base font-medium tracking-tight">{d.title}</h2>
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="flex flex-col gap-5">
          <Field label="Title">
            <Input
              defaultValue={d.title}
              onBlur={(e) =>
                e.target.value !== d.title &&
                e.target.value.trim() &&
                update.mutate({ id: d.id, patch: { title: e.target.value.trim() } })
              }
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Status">
              <Select
                value={d.status}
                onValueChange={(v) => update.mutate({ id: d.id, patch: { status: v as Status } })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Decided">
              <p className="text-muted-foreground pt-1 text-xs">
                {d.decided_at ? format(new Date(d.decided_at), "d MMM yyyy") : "—"}
              </p>
            </Field>
          </div>

          <Field label="Alternatives considered">
            <Textarea
              defaultValue={d.alternatives ?? ""}
              rows={3}
              onBlur={(e) =>
                e.target.value !== (d.alternatives ?? "") &&
                update.mutate({ id: d.id, patch: { alternatives: e.target.value } })
              }
            />
          </Field>

          <Field label="Rationale">
            <Textarea
              defaultValue={d.rationale ?? ""}
              rows={6}
              onBlur={(e) =>
                e.target.value !== (d.rationale ?? "") &&
                update.mutate({ id: d.id, patch: { rationale: e.target.value } })
              }
            />
          </Field>

          <Field label="Discussed in">
            {meetingsQ.data && meetingsQ.data.length > 0 ? (
              <ul className="flex flex-col gap-1 text-sm">
                {meetingsQ.data.map((m) => (
                  <li key={m.id}>
                    <Link to="/meetings/$id" params={{ id: m.id }} className="hover:underline">
                      <span className="text-muted-foreground text-[11px]">
                        {format(new Date(m.meeting_at), "d MMM")} ·{" "}
                      </span>
                      {m.title}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground text-xs italic">
                Not yet discussed in any logged meeting.
              </p>
            )}
          </Field>

          <p className="text-muted-foreground border-border/40 mt-2 border-t pt-3 font-mono text-[10px] tracking-wider">
            Last updated {format(new Date(d.updated_at), "d MMM, HH:mm")}
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-muted-foreground text-[10px] tracking-wider uppercase">{label}</Label>
      {children}
    </div>
  );
}
